const u = up.util;
const e = up.element;
up.Change.UpdateSteps = class UpdateSteps extends up.Change.Addition {
    constructor(options) {
        super(options);
        this._steps = u.copy(u.assert(options.steps));
        this._noneOptions = options.noneOptions || {};
    }
    execute(responseDoc) {
        this.responseDoc = responseDoc;
        this._steps = responseDoc.selectSteps(this._steps);
        this._steps = responseDoc.commitSteps(this._steps);
        return [
            ...this._executeSteps(),
            this._assertLayersAlive(),
        ];
    }
    _executeSteps() {
        if (this._steps.length) {
            return this._steps.toReversed().map((step) => this._executeStep(step)).toReversed();
        }
        else {
            return this._executeNoSteps();
        }
    }
    _assertLayersAlive() {
        return {
            verifyFinished: () => {
                for (let step of this._steps) {
                    step.layer.assertAlive();
                }
            }
        };
    }
    _executeStep(step) {
        switch (step.placement) {
            case 'swap': {
                let keepPlan = up.fragment.keepPlan(step);
                if (keepPlan) {
                    return this._executeKeepEverythingStep(step, keepPlan);
                }
                else {
                    return this._executeSwapStep(step);
                }
            }
            case 'content': {
                return this._executeSwapChildrenStep(step);
            }
            case 'before':
            case 'after': {
                return this._executeInsertChildrenStep(step);
            }
            default: {
                up.fail('Unknown placement: %o', step.placement);
            }
        }
    }
    _executeNoSteps() {
        this._handleFocus(null, this._noneOptions);
        this._handleScroll(null, this._noneOptions);
        return [{}];
    }
    _executeKeepEverythingStep(step, keepPlan) {
        if (!keepPlan)
            return;
        up.fragment.emitKept(keepPlan);
        this._handleFocus(step.oldElement, step);
        this._handleScroll(step.oldElement, step);
        return {};
    }
    _executeSwapStep(step) {
        var _a;
        const parent = (_a = step.parentElement) !== null && _a !== void 0 ? _a : step.oldElement.parentNode;
        up.fragment.markAsDestroying(step.oldElement);
        this._preserveDescendantKeepables(step);
        let compilePromise;
        let morphPromise = up.motion.morph(step.oldElement, step.newElement, step.transition, Object.assign(Object.assign({}, step), { afterInsert: () => {
                this._restoreDescendantKeepables(step);
                compilePromise = this._welcomeElement(step.newElement, step);
                this._finalizeDescendantKeepables(step);
                this._handleFocus(step.newElement, step);
                this._handleScroll(step.newElement, step);
            }, beforeRemove: () => {
                up.script.clean(step.oldElement, { layer: step.layer });
            }, afterRemove: () => {
                var _a;
                up.fragment.emitDestroyed(step.oldElement, { parent, log: false });
                (_a = step.afterRemove) === null || _a === void 0 ? void 0 : _a.call(step);
            } }));
        return {
            fragments: [step.newElement],
            finished: Promise.all([morphPromise, compilePromise]),
        };
    }
    _executeSwapChildrenStep(step) {
        let newChildren = [...step.newElement.children];
        let oldWrapper = e.wrapChildren(step.oldElement);
        let newWrapper = e.wrapChildren(step.newElement);
        let wrapperResult = this._executeSwapStep(Object.assign(Object.assign({}, step), { placement: 'swap', parentElement: step.oldElement.parentNode, oldElement: oldWrapper, newElement: newWrapper, focus: false, afterRemove: () => {
                e.unwrap(newWrapper);
                this._handleFocus(step.oldElement, step);
            } }));
        return {
            fragments: newChildren,
            finished: wrapperResult.finished,
        };
    }
    _executeInsertChildrenStep(step) {
        let newChildren = [...step.newElement.children];
        let wrapper = e.wrapChildren(step.newElement);
        let position = step.placement === 'before' ? 'afterbegin' : 'beforeend';
        step.oldElement.insertAdjacentElement(position, wrapper);
        let compilePromise = this._welcomeElement(wrapper, step);
        this._handleScroll(wrapper, step);
        let animatePromise = up.animate(wrapper, step.animation, Object.assign(Object.assign({}, step), { onFinished: () => {
                e.unwrap(wrapper);
                const focusElement = e.leadingElement(step.oldElement.childNodes) || step.oldElement;
                this._handleFocus(focusElement, step);
            } }));
        return {
            fragments: newChildren,
            finished: Promise.all([compilePromise, animatePromise]),
        };
    }
    _welcomeElement(element, step) {
        this.responseDoc.finalizeElement(element);
        this.setReloadAttrs(step);
        return up.hello(element, step);
    }
    _preserveDescendantKeepables(step) {
        const descendantKeepPlans = [];
        if (step.keep) {
            for (let keepable of step.oldElement.querySelectorAll('[up-keep]')) {
                let keepPlan = up.fragment.keepPlan(Object.assign(Object.assign({}, step), { oldElement: keepable, descendantsOnly: true }));
                if (keepPlan) {
                    const keepableClone = keepable.cloneNode(true);
                    keepable.insertAdjacentElement('beforebegin', keepableClone);
                    keepable.classList.add('up-keeping');
                    up.script.block(keepPlan.newElement);
                    let viewports = up.viewport.subtree(keepPlan.oldElement);
                    keepPlan.revivers = u.map(viewports, function (viewport) {
                        let cursorProps = up.viewport.copyCursorProps(viewport);
                        return () => up.viewport.copyCursorProps(cursorProps, viewport);
                    });
                    if (this._willChangeBody()) {
                        keepPlan.newElement.replaceWith(keepable);
                    }
                    else {
                        document.body.append(keepable);
                    }
                    descendantKeepPlans.push(keepPlan);
                }
            }
        }
        step.descendantKeepPlans = descendantKeepPlans;
    }
    _restoreDescendantKeepables(step) {
        for (let keepPlan of step.descendantKeepPlans) {
            keepPlan.newElement.replaceWith(keepPlan.oldElement);
            for (let reviver of keepPlan.revivers) {
                reviver();
            }
        }
    }
    _finalizeDescendantKeepables(step) {
        for (let keepPlan of step.descendantKeepPlans) {
            keepPlan.oldElement.classList.remove('up-keeping');
            up.fragment.emitKept(keepPlan);
        }
    }
    _willChangeBody() {
        return u.some(this._steps, (step) => step.oldElement.matches('body'));
    }
    _handleFocus(fragment, options) {
        const fragmentFocus = new up.FragmentFocus(Object.assign(Object.assign({}, options), { fragment, autoMeans: up.fragment.config.autoFocus }));
        fragmentFocus.process(options.focus);
    }
    _handleScroll(fragment, options) {
        var _a;
        const scrolling = new up.FragmentScrolling(Object.assign(Object.assign({}, options), { fragment, autoMeans: up.fragment.config.autoScroll }));
        scrolling.process((_a = options.scrollMap) !== null && _a !== void 0 ? _a : options.scroll);
    }
};
